import { useCallback, useRef } from 'react';
import { ACTIONS, CallBackProps, LIFECYCLE, STATUS } from 'react-joyride';

interface UseJoyrideCallbackOptions {
  onFinished: () => void;
  onSkipped: () => void;
  onClosed: () => void;
  onResumed: () => void;
}

/**
 * Minimal Joyride callback — detects FINISHED, SKIPPED, CLOSE, and beacon click.
 * All tour completion / sequence logic is handled in TourProvider directly.
 *
 * NO setState during step transitions to avoid floater repositioning bugs.
 */
export function useJoyrideCallback({ onFinished, onSkipped, onClosed, onResumed }: UseJoyrideCallbackOptions) {
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const onSkippedRef = useRef(onSkipped);
  onSkippedRef.current = onSkipped;

  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  const onResumedRef = useRef(onResumed);
  onResumedRef.current = onResumed;

  const handleCallback = useCallback((data: CallBackProps) => {
    const { status, action, lifecycle } = data;

    // Close button — hide tooltip, show beacon
    if (action === ACTIONS.CLOSE) {
      onClosedRef.current();
      return;
    }

    // Beacon clicked — user is resuming the tour
    if (lifecycle === LIFECYCLE.TOOLTIP && action === ACTIONS.START) {
      onResumedRef.current();
    }

    if (status === STATUS.FINISHED) {
      onFinishedRef.current();
    }

    if (status === STATUS.SKIPPED) {
      onSkippedRef.current();
    }
  }, []);

  return { handleCallback };
}
